import React, { useEffect, useRef, useState, useContext, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  UserDetails,
  setEncryptedValue,
  internalServerErrorCode,
  getLocalStorageBoolean,
  backToDashboardLink,
  NavigateConst,
  DeviceTypes,
  useDeviceType,
  apiResponseSafeCheck,
  safeJsonParse,
  getDecryptedValue,
  generateHashedKey,
  SortField,
  useClaimsSortFilter,
  domainForSegment,
  useAnalytics,
} from 'common';
import { getClaimDateRange, getCustomDateRange } from 'common/utils/ClaimUtils';
import { normalizeToISODate } from 'common/utils/DateFormat';
import {
  useMemberIDCard,
  useMyDepedentAccess,
  useMedicalClaimSearch,
  ClaimResponse as Claim,
  useGetDependentAccess
} from '@sentara-health/sentara-api-hooks-core';

import { GlobalContext, isAvMed } from 'context';
import {
  Button,
  Headings,
  Paragraph,
  FilterIconWhiteWithBg,
  FilterIconWhite,
  ContactInfo,
  Link,
  Loader,
  ChatMinimizeIcon,
  SelectDropdown,
} from '@sentara-health/optimahealth_web';
import MemberSelectDropdown from './MemberSelectDropdown';
import { logoutErrorHandler } from '../../common/utils/LogoutErrorHandler';
import { ClaimsFilter } from './ClaimsFilter';
import {
  ClaimsFilterState,
  initialClaimsFilterState,
} from '../../common/types/claimsFilters';
import { ClaimsAccordionWrapper } from './ClaimsAccordianWrapper';
import { useIndexedDBSearch } from './useIndexedDBSearch';
import { useTypeAheadSearchMedicalClaims } from './useTypeAheadSearchMedicalClaims';
import ViewTaxDropDown from './ClaimsAccordianWrapper/ViewTaxDropDown';
import { DependentConsentModals } from '../authorization/DependentConsentModals';
import { useDependentConsent } from '../authorization/useDependentConsent';
import DependentAccessCheckbox from '../authorization/DependentAccessCheckbox';

type Member = {
  memberName: string;
  memberId: string;
  mId: string;
};

type ClaimsSearchTrackingSource = 'smart-search' | 'filter';

const BEHAVIORAL_CLAIMS_PATH_END = 'behavioral-claims';
const MEDICAL_CLAIMS_PATH_END = 'medical-claims';

const SuperMedicalClaims: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const claimsSortOptions = [
    { label: t('medicalClaims.serviceType'), value: 'serviceType' },
    { label: t('common.date'), value: 'dateOfService' },
    { label: t('common.status'), value: 'status' },
    { label: t('medicalClaims.location'), value: 'location' },
    { label: t('common.amount'), value: 'amountOwed' },
    { label: t('medicalClaims.providerName'), value: 'providerName' },
  ];

  // ----- Context -----
  const { loginMID, primaryUser, loginMemberId, getFeature, serviceNumber } =
    useContext(GlobalContext);
  const { trackContentListing } = useAnalytics();
  const [showMedical, setShowMedical] = useState(false);

  const trackClaimsAdobeEvent = useCallback(
    (
      contentId: string,
      contentTitle: string,
      itemPosition: number,
      additionalFields: Record<string, string | number | undefined> = {}
    ) => {
      trackContentListing(
        contentId,
        contentTitle,
        itemPosition,
        `${domainForSegment}${showMedical ? '/home/medical-claims' : '/home/behavioral-claims'}`,
        { extraFields: additionalFields }
      );
    },
    [trackContentListing, showMedical]
  );

  const trackClaimsLoadError = useCallback(
    (_errorCode?: string | number) => {
      if (showMedical) {
        trackClaimsAdobeEvent('1218', 'Medical_claim_load_error_view', 11);
      } else {
        trackClaimsAdobeEvent('1225', 'Behavioral_claim_load_error_view', 17);
      }
    },
    [showMedical, trackClaimsAdobeEvent]
  );
  // Store original claims data to reset when needed
  const originalClaimsData = useRef<Claim[]>([]);
  // ----- API hooks -----
  const { medicalClaimSearch } = useMedicalClaimSearch();
  const { memberIDCardType } = useMemberIDCard();
  const { GetDependentAccessType } = useGetDependentAccess();;

  // ----- UI state -----
  const [selectedSortField, setSelectedSortField] = useState<SortField | ''>(
    ''
  );
  const [showSpouse, setShowSpouse] = useState(false);
  const [showParent, setShowParent] = useState(false);
  const { MyDepedentAccessType } = useMyDepedentAccess();
  const [membersTable, setMembersTable] = useState<Member[]>([]);
  const [phone, setPhone] = useState<{
    data?: { benefitPhone1800?: string; benefitPhoneLocal?: string };
  }>({});

  // selection state: UI MID + legacy memberId
  const [selectedMID, setSelectedMID] = useState<string | null>(
    loginMID ?? null
  );
  const [memberId, setMemberId] = useState<string | null>(
    loginMemberId ?? loginMID ?? null
  );

  // ID used for fetching claims (actual API memberId)
  const [apiMemberId, setApiMemberId] = useState<string | null>(
    loginMemberId ?? null
  );

  // misc state
  const [medicalClaims, setMedicalClaims] = useState<Claim[]>([]);
  const [loader, setLoader] = useState(false);
  const [hasApiSucceeded, setHasApiSucceeded] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [showFilter, setShowFilter] = useState(false);
  const consent = useDependentConsent({
    checkMemberAccess,
    membersTable,
  });
  const {
    dependentAccess,
    setDependentAccess,
    handleDependentAccessChange,
    openViewTermsModal,
    openErrorModal,
  } = consent;
  const [appliedFilters, setAppliedFilters] = useState<ClaimsFilterState>(
    initialClaimsFilterState
  );

  const {
    queriedClaims,
    displayedClaims,
    intersectionObserverTargetRef,
    hasMoreItemsToLoad,
  } = useClaimsSortFilter({
    claims: medicalClaims,
    selectedSortField,
    appliedFilters,
    pageSize: 10,
  });

  // ----- Derived -----
  const hasSingleMember = (membersTable?.length ?? 0) <= 1;

  // ----- Smart Search Initialization -----
  const { indexedDbInit } = useIndexedDBSearch();

  const { clearIndexedDB } = useIndexedDBSearch();
  // Keep the latest search function (avoids stale closures)
  const searchFnRef = useRef(medicalClaimSearch);
  useEffect(() => {
    searchFnRef.current = medicalClaimSearch;
  }, [medicalClaimSearch]);

  const hasInitializedMemberIds = useRef(false);
  useEffect(() => {
    if (hasInitializedMemberIds.current) return;
    setSelectedMID(loginMID ?? null);
    hasInitializedMemberIds.current = true;
  }, [loginMID]);

  const lastFetchedApiMemberId = useRef<string | null>(null);
  const requestCounterRef = useRef(0);
  const pendingClaimsSearchTracking = useRef<ClaimsSearchTrackingSource | null>(
    null
  );

  const markClaimsSearchTrackingPending = useCallback(
    (source: ClaimsSearchTrackingSource) => {
      pendingClaimsSearchTracking.current = source;
    },
    []
  );

  const setClaimsData = useMemo(
    () => (data: Claim | Claim[]) => {
      const claimsArray = Array.isArray(data) ? data : [data];
      setMedicalClaims(claimsArray);
      // Store original data for reset functionality
      originalClaimsData.current = claimsArray;
    },
    []
  );

  const handleCachedClaims = (cached: string | null, cacheKey: string) => {
    if (!cached) return false;
    try {
      const parsedData = JSON.parse(cached);
      setClaimsData(parsedData);
      indexedDbInit(parsedData);
      setLoader(false);
      setErrorMessage('');
      setHasApiSucceeded(true);
      return true;
    } catch {
      setEncryptedValue(cacheKey, '');
      return false;
    }
  };

  const handleApiErrors = (apiResponse: any) => {
    if (apiResponse?.error || apiResponse?.errorCode) {
      if (logoutErrorHandler(apiResponse.errorCode ?? apiResponse.error))
        return true;
      if (apiResponse?.errorCode === internalServerErrorCode || apiResponse?.error) {
        setErrorMessage(t('common.internalServerError') ?? '');
      }
      trackClaimsLoadError(apiResponse?.errorCode);
      setMedicalClaims([]);
      return true;
    }
    return false;
  };

  const fetchClaimsById = React.useCallback(
    async (
      id: string | null,
      customDateParams?: { startDate: string; endDate: string }
    ) => {
      if (!id) return;

      const memberCacheKey = await generateHashedKey(
        `${showMedical ? 'Medical' : 'Behavioral'}_Claims`,
        id
      );
      // For standard 18-month fetches
      if (!customDateParams) {
        if (lastFetchedApiMemberId.current === id) return;
        lastFetchedApiMemberId.current = id;
      }
      const myRequestId = ++requestCounterRef.current;
      if (!customDateParams) setMedicalClaims([]);

      setLoader(true);
      setErrorMessage('');
      setHasApiSucceeded(false);
      try {
  // Skip cache for custom date fetch (we need a superset from userStart -> today)
        if (!customDateParams) {
          const cached = await getDecryptedValue(memberCacheKey);
          if (handleCachedClaims(cached, memberCacheKey)) return;
        }

        const { startDate, endDate } = customDateParams || getClaimDateRange();
        const requestBody = {
          identifier: [{ system: 'MemberId', value: id }],
          claimType: showMedical ? 0 : 1,
          startDate,
          endDate,
        };
        const apiResponse = await searchFnRef.current(requestBody);
        if (apiResponse?.error || apiResponse?.errorCode) {
          if (logoutErrorHandler(apiResponse.errorCode ?? apiResponse.error))
            return;
          if (apiResponse?.errorCode === internalServerErrorCode || apiResponse?.error) {
            setErrorMessage(t('common.internalServerError') ?? '');
          }
          trackClaimsLoadError(apiResponse?.errorCode);
          setMedicalClaims([]);
          return;
        }

        if (requestCounterRef.current !== myRequestId) return;
        if (handleApiErrors(apiResponse)) return;
        // Set the claims data if successful
        setHasApiSucceeded(true);
        if (apiResponse?.data) {
          setEncryptedValue(memberCacheKey, JSON.stringify(apiResponse.data));
          const normalizedData = Array.isArray(apiResponse.data)
            ? apiResponse.data
            : [apiResponse.data];
          setClaimsData(normalizedData);
          indexedDbInit(normalizedData);
        } else {
          originalClaimsData.current = [];
          clearIndexedDB();
        }
      } catch (error) {
        if (requestCounterRef.current !== myRequestId) return;
        if (logoutErrorHandler(error)) return;
        setErrorMessage(t('common.internalServerError') ?? '');
        trackClaimsLoadError();
        setMedicalClaims([]);
      } finally {
        if (requestCounterRef.current === myRequestId) {
          setLoader(false);
        }
      }
    },
    [showMedical, trackClaimsLoadError]
  );

  const handleApplyFilter = useCallback(async (filters: ClaimsFilterState) => {
    // Detect custom date range
    const { customFromDate, customToDate } = filters;
    // Normalize UI dates to API format (YYYY-MM-DD)
    const customFrom = normalizeToISODate(customFromDate);
    const customTo = normalizeToISODate(customToDate);

    if (customFrom && customTo) {
      const dateRangeInfo = getCustomDateRange(customFrom, customTo);
      if (dateRangeInfo.shouldCallApi && apiMemberId) {
        setAppliedFilters(filters);
        setShowFilter(false);
        // start date = userStartDate, end date = today
        await fetchClaimsById(apiMemberId, {
          startDate: dateRangeInfo.startDate,
          endDate: dateRangeInfo.endDate,
        });
        markClaimsSearchTrackingPending('filter');
        setAppliedFilters((currentFilters) => ({ ...currentFilters }));
        return;
      }
    }
    markClaimsSearchTrackingPending('filter');
    setAppliedFilters(filters);
    setShowFilter(false);
  }, [apiMemberId, fetchClaimsById, markClaimsSearchTrackingPending]);

  const handleClearAll = useCallback(() => {
    setAppliedFilters(initialClaimsFilterState);
  }, []);

  const handleCloseFilter = useCallback(() => {
    setShowFilter(false);
  }, []);

  useEffect(() => {
    if (!apiMemberId) return;
    if (membersTable.length > 0) {
      const timer = setTimeout(() => {
        lastFetchedApiMemberId.current = null;
        fetchClaimsById(apiMemberId);
        // Reset smart search when member or claim type changes
        clearTypeAheadSearch();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [apiMemberId, fetchClaimsById, showMedical, membersTable.length]);

  useEffect(() => {
    const next = loginMemberId ?? loginMID ?? null;
    setMemberId((prev) => (prev !== next ? next : prev));
  }, [loginMemberId, loginMID]);

  async function loadMembers() {
    setLoader(true);
    try {
      const membersCacheKey = loginMemberId ? `Members_Claims_${loginMemberId}` : 'Members_Claims';
      const cachedMembers = await getDecryptedValue(membersCacheKey);
      let result;
      
      if (cachedMembers) {
        try {
          result = JSON.parse(cachedMembers);
        } catch {
          setEncryptedValue(membersCacheKey, '');
          result = null;
        }
      }
      
      if (!result) {
        result = await memberIDCardType('claims');
        if (logoutErrorHandler?.(result?.errorCode ?? result?.error)) {
          setLoader(false);
          return;
        }
        if (result?.data && !result?.error && !result?.errorCode) {
          setEncryptedValue(membersCacheKey, JSON.stringify({ data: result.data }));
        }
      }
      const { data, error, errorCode } = result ?? {};
      let members: any[] = [];
      if (data?.members) {
        members = Array.isArray(data.members) ? data.members : [data.members];
      }
      setMembersTable(members);
      
      // Reset permission flags before setting new values
      setShowParent(false);
      setShowSpouse(false);
      
      if (data?.parentPermission === true) {
        setShowParent(true);
      } else if (data?.spoucePermission === true) {
        setShowSpouse(true);
      }
      if (errorCode || error) {
        // API returned an error code/message: stop the loader and show the error modal.
        // Pass the full result so openErrorModal can read result.errorCode and route
        // 401/440 to logout, displaying the modal for other codes.
        setLoader(false);
        openErrorModal(result);
      }

      if (members.length > 0) {
        const savedMID = await getDecryptedValue('selectedMemberMID_Claims');
        let memberToSelect;

        if (savedMID) {
          memberToSelect = members.find((m) => m.mId === savedMID);
        }

        if (!memberToSelect && loginMID) {
          memberToSelect = members.find((m) => m.mId === loginMID);
        }

        if (!memberToSelect) {
          memberToSelect = members[0];
        }

        if (memberToSelect) {
          setSelectedMID(memberToSelect.mId);
          setMemberId(memberToSelect.mId);
          setApiMemberId(memberToSelect.memberId);
          setEncryptedValue(UserDetails.memberIdData, memberToSelect.mId);
          setEncryptedValue('selectedMemberMID_Claims', memberToSelect.mId);
        }
      }
    } catch (err) {
      // Unexpected/thrown error: stop the loader and surface it via the error modal.
      setLoader(false);
      openErrorModal(err);
    } finally {
      setLoader(false);
      setPhone(
        typeof serviceNumber === 'string'
          ? safeJsonParse(serviceNumber, {})
          : { data: serviceNumber }
      );
    }
  }

  const lastInitializedPath = useRef<string | null>(null);
  useEffect(() => {
    const path = location.pathname;
    if (lastInitializedPath.current === path) return;
    lastInitializedPath.current = path;
    (async () => {
      setShowMedical(path.endsWith(MEDICAL_CLAIMS_PATH_END));
      if (path.endsWith(BEHAVIORAL_CLAIMS_PATH_END)) setShowMedical(false);
      setAppliedFilters(initialClaimsFilterState);
      setSelectedSortField('');
      const dependentAccessCacheKey = loginMemberId ? `DependentAccess_${loginMemberId}` : 'DependentAccess';
      const cachedDependentAccess = await getDecryptedValue(dependentAccessCacheKey);
      
      if (cachedDependentAccess) {
        try {
          const parsedCache = JSON.parse(cachedDependentAccess);
          setDependentAccess(parsedCache?.data?.isDependentAccess ?? false);
        } catch {
          setEncryptedValue(dependentAccessCacheKey, '');
          setDependentAccess(false);
        }
      } else {
        const response = await GetDependentAccessType();
        if (logoutErrorHandler(response?.errorCode ?? response?.error)) return;
        setDependentAccess(response?.data?.isDependentAccess ?? false);
        if (response?.data && !response?.error && !response?.errorCode) {
          setEncryptedValue(dependentAccessCacheKey, JSON.stringify({ data: response.data }));
        }
      }
      await loadMembers();
    })();
  }, [location.pathname]);
  const setMemberDetails = async (MID: string) => {
    setSelectedMID(MID); // <-- critical: update the controlled value
    setMemberId(MID);
    setEncryptedValue(UserDetails.memberIdData, MID);
    setEncryptedValue('selectedMemberMID_Claims', MID);

    const mapped = membersTable.find((m) => m.mId === MID)?.memberId ?? null;
    if (mapped && mapped !== apiMemberId) {
      lastFetchedApiMemberId.current = null;
      setApiMemberId(mapped);
      setAppliedFilters(initialClaimsFilterState);
      setSelectedSortField('');
    }
  };

  // (Optional) If loginMID changes at runtime, sync the dropdown selection too
  useEffect(() => {
    if (loginMID) setSelectedMID(loginMID);
  }, [loginMID]);

  useEffect(() => {
    if (
      !getLocalStorageBoolean(backToDashboardLink) &&
      loginMID &&
      membersTable.length > 0
    ) {
      setMemberDetails(loginMID as string);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loginMID, membersTable.length]);

  useEffect(() => {
    if (location.pathname === '/home/medical-claims') {
      localStorage.removeItem('selectedOption');
    }
  }, [location.pathname]);

  const deviceType = useDeviceType();
  const isMobile = deviceType === DeviceTypes.MOBILE;
  const memberName = membersTable.find(
    (m) => m.memberId === apiMemberId
  )?.memberName;

  const { renderTypeAheadSearch, searchNoRecords, searchInputValue, clearTypeAheadSearch, performSearchAndApply } =
    useTypeAheadSearchMedicalClaims({
      originalClaimsData,
      setMedicalClaims,
      onSmartSearchSelect: () => {
        setAppliedFilters(initialClaimsFilterState);
      },
      onSearchSubmitted: () => {
        markClaimsSearchTrackingPending('smart-search');
      },
      isMobile,
    });

  useEffect(() => {
    if (!pendingClaimsSearchTracking.current || loader) return;
    pendingClaimsSearchTracking.current = null;

    if (errorMessage) return;

    if (queriedClaims.length > 0) {
      trackClaimsAdobeEvent('1206', `${showMedical ? 'Medical' : 'Behavioral'}_claim_search_success`, 2);
    } else {
      trackClaimsAdobeEvent('1208', `${showMedical ? 'Medical' : 'Behavioral'}_claim_search_failure`, 3);
    }
  }, [errorMessage, loader, queriedClaims.length, trackClaimsAdobeEvent]);

  const handleClick = (action: 'view' | 'pay', id: string) => {
    if (action === 'view') {
      trackClaimsAdobeEvent('1207', `${showMedical ? 'Medical' : 'Behavioral'}_Claim_details_button_click`, 0);
      navigate(NavigateConst.claimsSummary, {
        state: { claimId: id, memberId: apiMemberId,mId: memberId, memberName, showMedical },
      });
    } else if (action === 'pay') {
      trackClaimsAdobeEvent('1212', `${showMedical ? 'Medical' : 'Behavioral'}_Claims_pay_your_bill_click`, 6);
      navigate(NavigateConst.paySentaraBillURL);
    }
  };

  async function checkMemberAccess(value: boolean) {
    setLoader(true);
    try {
      const userId = ((await getDecryptedValue(UserDetails.usedId)) ?? '') as string;
      const response = await MyDepedentAccessType({
        allowAccess: value,
        userId,
      });
      
      if (logoutErrorHandler(response?.errorCode ?? response?.error)) {
        setLoader(false);
        return;
      }
      
      const dependentAccessCacheKey = loginMemberId ? `DependentAccess_${loginMemberId}` : 'DependentAccess';
      if (response?.data && !response?.error && !response?.errorCode) {
        setEncryptedValue(dependentAccessCacheKey, JSON.stringify({ data: response.data }));
      }
    } catch (err) {
      if (!logoutErrorHandler(err)) {
        setErrorMessage(t('common.internalServerError') ?? '');
      }
    } finally {
      setLoader(false);
    }
  }
  
  const handleResetToAllClaims = () => {
    clearTypeAheadSearch();
    setAppliedFilters(initialClaimsFilterState);
    setSelectedSortField('dateOfService');
  };

  const handleRecentClaims = () => {
    trackClaimsAdobeEvent('1214', `${showMedical ? 'Medical' : 'Behavioral'}_Claims_recent_claims_click`, 8);
    handleResetToAllClaims();
  };

  const handleAllClaims = () => {
    trackClaimsAdobeEvent('1213', `${showMedical ? 'Medical' : 'Behavioral'}_Claims_all_claims_click`, 7);
    handleResetToAllClaims();
  };

  const handlePendingClaims = async () => {
    trackClaimsAdobeEvent('1215', `${showMedical ? 'Medical' : 'Behavioral'}_Claims_pending_claims_click`, 9);
    clearTypeAheadSearch();
    setAppliedFilters(initialClaimsFilterState);
    setSelectedSortField('dateOfService');
    await performSearchAndApply('Pending');
  };

  const handleDeniedClaims = async () => {
    trackClaimsAdobeEvent('1216', `${showMedical ? 'Medical' : 'Behavioral'}_Claims_view_denied_claims_click`, 10);
    clearTypeAheadSearch();
    setAppliedFilters(initialClaimsFilterState);
    setSelectedSortField('dateOfService');
    await performSearchAndApply('Denied');
  };

  if (loader) return <Loader />;

  return (
    <div>
      {isMobile ? (
        <Button
          iconLeft={<ChatMinimizeIcon />}
          className="mb-3"
          data-testid="Backbtn"
          variant="icon_btn"
          onClick={() => navigate(NavigateConst.dashboard)}
        >
          {t('common.back')}
        </Button>
      ) : null}
      <div className="not-print">
        {/* Responsive header: smaller on mobile, Year dropdown full-width on mobile */}
        <div className="row align-items-center gy-2 mb-3 justify-content-md-between d-flex">
          <div className="col-12 col-md">
            <Headings
              className="text-nowrap"
              level={2}
              text={
                showMedical
                  ? t('medicalClaims.medcialClaimTitle')
                  : t('medicalClaims.behavioralClaimsTitle')
              }
              variant="semiBold"
              dataTestId={
                showMedical ? 'medicalClaimsHeader' : 'behavioralClaimsHeading'
              }
            />
          </div>
          {showMedical ? (
            <>
              <div className="col-12 col-md-auto d-flex align-items-center gap-2">
                <div className="flex-grow-1">
                  <MemberSelectDropdown
                    members={membersTable}
                    memberId={selectedMID}
                    setMemberId={setMemberDetails}
                    primaryUser={primaryUser}
                    loginMID={loginMID ?? undefined}
                    disableWhenSingleMember={hasSingleMember}
                    onMemberSelect={() =>
                      trackClaimsAdobeEvent(
                        '1205',
                        `${showMedical ? 'Medical' : 'Behavioral'}_Member_details_Button_click`,
                        1
                      )
                    }
                  />
                </div>
                {getFeature?.ViewTaxYearSummary ? <ViewTaxDropDown /> : null}
              </div>

              {(showSpouse || showParent) &&
              getFeature?.Allowspouseviewclaims && selectedMID === loginMID ? (
                  <DependentAccessCheckbox
                    accessType={showSpouse ? 'spouse' : 'parent'}
                    label={
                      showSpouse
                        ? t('medicalClaims.allowAccessSpouse')
                        : t('medicalClaims.allowAccessParent')
                    }
                    dependentAccess={dependentAccess}
                    onAccessChange={handleDependentAccessChange}
                    onViewTerms={openViewTermsModal}
                    viewTermsLabel={t('common.viewTerms')}
                    viewTermsDataTestId="superMedicalClaimsViewTermsButton"
                    className="d-flex align-items-center mb-2"
                    showViewTerms={getFeature?.CheckboxPopup === true}
                  />
              ) : null}
            </>
          ) : null}
        </div>
      </div>

      {renderTypeAheadSearch()}

      <div className="mt-4 mb-4">
        <div className="d-flex align-items-center gap-3 justify-content-md-end">
          <div className="d-flex align-items-center flex-nowrap flex-grow-1 flex-md-grow-0 py-1">
            <Paragraph
              text={t('medicalClaims.sortBy')}
              variant="regular"
              className="me-2 m-0 p-0 text-nowrap"
            />
            <SelectDropdown
              dataTestId="claims-sort-select"
              className="form-select form-select-sm flex-grow-1 flex-md-grow-0"
              placeholder={t('memberIdCard.select')}
              value={selectedSortField}
              items={claimsSortOptions}
              onItemSelect={(item: { value: string }) =>
                setSelectedSortField(item.value as SortField | '')
              }
            />
          </div>

          <div className="py-1">
            <Button
              variant={isMobile ? 'icon_btn' : 'primary_btn'}
              iconLeft={isMobile ? null : <FilterIconWhite fill="white" />}
              onClick={() => setShowFilter(true)}
            >
              {isMobile ? (
                <FilterIconWhiteWithBg fill="white" background="secondary" />
              ) : (
                t('medicalClaims.filterTitle')
              )}
            </Button>
          </div>
        </div>
      </div>

      <ClaimsFilter
        visible={showFilter}
        onClose={handleCloseFilter}
        onApply={handleApplyFilter}
        onCancel={handleCloseFilter}
        onClearAll={handleClearAll}
        claims={medicalClaims}
        appliedFilters={appliedFilters}
      />

      {searchNoRecords && (searchInputValue?.length ?? 0) > 0 && (
        <div className="py-4 no-results" data-testid="noResults">
          <Paragraph
            text={t('medicalClaims.smartSearchNoRecord')}
            variant="bold"
          />
          <Paragraph
            text={t('medicalClaims.trySearching')}
            variant="regular"
            className="mb-3"
          />
          <div className="d-flex flex-wrap gap-2 align-items-start">
            <Button
              key="all-claims"
              variant="secondary_btn"
              onClick={handleAllClaims}
              data-testid="allClaimsButton"
              className="flex-shrink-0 w-auto"
            >
              {t('medicalClaims.allClaims')}
            </Button>
            <Button
              key="recent-claims"
              variant="secondary_btn"
              onClick={handleRecentClaims}
              data-testid="recentClaimsButton"
              className="flex-shrink-0 w-auto"
            >
              {t('medicalClaims.recentClaims')}
            </Button>
            <Button
              key="pending-claims"
              variant="secondary_btn"
              onClick={handlePendingClaims}
              data-testid="pendingClaimsButton"
              className="flex-shrink-0 w-auto"
            >
              {t('medicalClaims.pendingClaims')}
            </Button>
            <Button
              key="denied-claims"
              variant="secondary_btn"
              onClick={handleDeniedClaims}
              data-testid="deniedClaimsButton"
              className="flex-shrink-0 w-auto"
            >
              {t('medicalClaims.deniedClaims')}
            </Button>
          </div>
        </div>
      )}

      {!isMobile && !searchNoRecords ? (
        <div data-testid="medicalExplanations" className="mt-4 mb-4">
          <Paragraph
            text={
              <>
                {showMedical
                  ? t('common.medicalClaimsExplanation')
                  : t('common.behavioral')}
              </>
            }
            variant="bold"
          />
        </div>
      ) : null}
      {errorMessage && (
        <div className="mb-4 text-center no-results" data-testid="errorMessage">
          {errorMessage}
        </div>
      )}

      {!errorMessage && queriedClaims.length === 0 && !loader && !searchNoRecords && hasApiSucceeded && (
        <div className="mb-4 text-center no-results" data-testid="noResults">
          {t('common.noResults')}
        </div>
      )}

      {!errorMessage && queriedClaims.length > 0 && !searchNoRecords && (
        <>
          <ClaimsAccordionWrapper
            claims={displayedClaims}
            onClick={handleClick}
          />

          {hasMoreItemsToLoad && <div ref={intersectionObserverTargetRef} />}
        </>
      )}

      {isAvMed() ? null : (
        <div className="black-color pt-4">
          <Paragraph
            text={t('medicalClaims.disclaimer1')}
            variant="regular"
            className="discList ml-3"
            data-testid="disclaimerHeader"
          />
          {apiResponseSafeCheck(phone?.data) ? (
            <Paragraph
              text={
                <>
                  {t('medicalClaims.disclaimer21')}{' '}
                  <ContactInfo
                    data-testid="contactInfo"
                    phoneData={{
                      phone1800: phone?.data?.benefitPhone1800,
                      phoneLocal: phone?.data?.benefitPhoneLocal,
                    }}
                  />
                  {t('medicalClaims.disclaimer22')}
                </>
              }
              variant="regular"
              className="discList ml-3"
              data-testid="disclaimerHeader"
            />
          ) : (
            <Paragraph
              text={t('medicalClaims.disclaimer23')}
              variant="regular"
              className="discList ml-3"
              data-testid="disclaimerHeader"
            />
          )}
          <Paragraph
            text={t('medicalClaims.disclaimer3')}
            variant="regular"
            className="discList ml-3"
            data-testid="disclaimerHeader"
          />
          <Paragraph text={t('medicalClaims.disclaimer4')} variant="regular" />
          <Paragraph
            text={
              <>
                {t('medicalClaims.disclaimer5')}
                <Link
                  to={`mailto:${t('medicalClaims.disclaimer6')}`}
                  className="ws-nowrap text-decoration-underline px-1"
                  data-testid="customerMailId"
                >
                  {t('medicalClaims.disclaimer6')}
                </Link>
                {apiResponseSafeCheck(phone?.data) ? (
                  <>
                    {t('medicalClaims.disclaimer7')}{' '}
                    <ContactInfo
                      data-testid="contactInfo"
                      phoneData={{
                        phone1800: phone?.data?.benefitPhone1800,
                        phoneLocal: phone?.data?.benefitPhoneLocal,
                      }}
                      className="ps-1"
                    />
                  </>
                ) : null}
              </>
            }
            variant="regular"
            data-testid="disclaimer5"
            className="mb-5"
          />
        </div>
      )}

      <DependentConsentModals consent={consent} />
    </div>
  );
};

export default SuperMedicalClaims;
